import React, { useState, useCallback } from 'react'
import { FileCheck, Clock, TrendingUp, Calendar, CheckCircle, AlertTriangle, User } from 'lucide-react'
import { PageHeader } from '@components/common'
import {
	MetricCard,
	ReportCard,
	ReportTable,
	ReportFilter,
	ReportExport,
	LineChart,
	DonutChart,
} from '@components/features/reports'
import { useReportData, useUserReport, useReportExport } from '@hooks'
import { useAuth } from '@context/AuthContext'
import './Reports.scss'

/**
 * Shared Reports Page
 *
 * Personal reports and analytics for individual users.
 * Shows task performance, approval history, and personal metrics.
 */
const Reports = () => {
	const { user } = useAuth()
	const [activeTab, setActiveTab] = useState('my-tasks')

	// Filters state
	const [filters, setFilters] = useState({
		dateRange: { preset: 'last_30_days' },
	})

	// Report data hooks - use applyFilters to update with new filter values
	const {
		data: myTasksData,
		loading: myTasksLoading,
		applyFilters: applyTaskFilters,
		refresh: refreshMyTasks,
	} = useUserReport(null, 'tasks', { initialFilters: filters })

	const {
		data: myApprovalsData,
		loading: myApprovalsLoading,
		applyFilters: applyApprovalFilters,
	} = useUserReport(null, 'approvals', { initialFilters: filters })

	const {
		data: performanceData,
		loading: performanceLoading,
		applyFilters: applyPerformanceFilters,
	} = useUserReport(null, 'performance', { initialFilters: filters })

	// Export hook
	const { exporting, exportReport } = useReportExport()

	// Handle filter change
	const handleFilterChange = useCallback((newFilters) => {
		setFilters(newFilters)
	}, [])

	// Handle filter apply - apply filters to all report hooks based on active tab
	const handleFilterApply = useCallback(() => {
		// Apply filters to all report types
		applyTaskFilters(filters)
		applyApprovalFilters(filters)
		applyPerformanceFilters(filters)
	}, [filters, applyTaskFilters, applyApprovalFilters, applyPerformanceFilters])

	// Handle export
	const handleExport = useCallback(async (format) => {
		await exportReport('my_tasks', format, filters)
	}, [exportReport, filters])

	// Filter config
	const filterConfig = [
		{
			key: 'status',
			label: 'Task Status',
			type: 'select',
			options: [
				{ value: 'not_started', label: 'Not Started' },
				{ value: 'in_progress', label: 'In Progress' },
				{ value: 'pending_review', label: 'Pending Review' },
				{ value: 'completed', label: 'Completed' },
			],
		},
	]

	// Tabs based on user role
	const tabs = [
		{ id: 'my-tasks', label: 'My Tasks', icon: <FileCheck size={16} /> },
		{ id: 'approvals', label: 'Approvals', icon: <CheckCircle size={16} /> },
		{ id: 'performance', label: 'Performance', icon: <TrendingUp size={16} /> },
	]

	return (
		<div className="shared-reports">
			<PageHeader
				title="My Reports"
				subtitle={`Personal analytics and task reports for ${user?.first_name || 'User'}`}
				actions={
					<ReportExport
						onExport={handleExport}
						loading={exporting}
					/>
				}
			/>

			{/* Filters */}
			<ReportFilter
				filters={filters}
				onChange={handleFilterChange}
				onApply={handleFilterApply}
				filterConfig={filterConfig}
				showDateRange={true}
			/>

			{/* Tabs */}
			<div className="reports-tabs">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						className={`reports-tabs__tab ${activeTab === tab.id ? 'reports-tabs__tab--active' : ''}`}
						onClick={() => setActiveTab(tab.id)}
					>
						{tab.icon}
						<span>{tab.label}</span>
					</button>
				))}
			</div>

			{/* Tab Content */}
			<div className="reports-content">
				{activeTab === 'my-tasks' && (
					<MyTasksTab
						data={myTasksData}
						loading={myTasksLoading}
						onRefresh={refreshMyTasks}
					/>
				)}

				{activeTab === 'approvals' && (
					<ApprovalsTab
						data={myApprovalsData}
						loading={myApprovalsLoading}
					/>
				)}

				{activeTab === 'performance' && (
					<PerformanceTab
						data={performanceData}
						loading={performanceLoading}
					/>
				)}
			</div>
		</div>
	)
}

// My Tasks Tab Component
const MyTasksTab = ({ data, loading, onRefresh }) => {
	const tasks = data?.data || data || {}
	const summary = tasks.summary || {}
	const taskList = tasks.tasks || tasks.data || []

	// Status badge renderer for better UX
	const renderStatusBadge = (status) => {
		const statusLabels = {
			not_started: 'Not Started',
			in_progress: 'In Progress',
			pending_review: 'Pending Review',
			completed: 'Completed',
		}
		return (
			<span className={`status-badge status-badge--${status}`}>
				{statusLabels[status] || status?.replace(/_/g, ' ')}
			</span>
		)
	}

	const taskColumns = [
		{ key: 'title', label: 'Task', sortable: true },
		{ key: 'project_name', label: 'Project', sortable: true },
		{
			key: 'workflow_status',
			label: 'Status',
			sortable: true,
			render: (value) => renderStatusBadge(value),
		},
		{ key: 'due_date', label: 'Due Date', sortable: true, format: 'date' },
		{
			key: 'is_overdue',
			label: 'Overdue',
			render: (value) => (value ? <AlertTriangle size={16} className="text-danger" /> : '-'),
		},
	]

	return (
		<div className="my-tasks-tab">
			{/* Key Metrics */}
			<div className="metric-cards-grid">
				<MetricCard
					title="Total Assigned"
					value={summary.total || taskList.length || 0}
					icon={<FileCheck size={20} />}
					variant="primary"
					loading={loading}
				/>
				<MetricCard
					title="Completed"
					value={summary.completed || 0}
					icon={<CheckCircle size={20} />}
					variant="success"
					loading={loading}
				/>
				<MetricCard
					title="In Progress"
					value={summary.in_progress || 0}
					icon={<Clock size={20} />}
					variant="info"
					loading={loading}
				/>
				<MetricCard
					title="Overdue"
					value={summary.overdue || 0}
					icon={<AlertTriangle size={20} />}
					variant={summary.overdue > 0 ? 'danger' : 'success'}
					loading={loading}
				/>
			</div>

			{/* Task Distribution */}
			<div className="reports-grid reports-grid--2">
				<ReportCard title="Task Status Distribution" loading={loading}>
					<DonutChart
						data={[
							{ label: 'Completed', value: summary.completed || 0, color: '#10b981' },
							{ label: 'In Progress', value: summary.in_progress || 0, color: '#3b82f6' },
							{ label: 'Not Started', value: summary.not_started || 0, color: '#6b7280' },
							{ label: 'Pending Review', value: summary.pending_review || 0, color: '#f59e0b' },
						]}
						showLegend={true}
						size={200}
					/>
				</ReportCard>

				<ReportCard title="Completion Rate" loading={loading}>
					<div className="completion-rate">
						<div className="completion-rate__value">
							{summary.completion_rate || 0}%
						</div>
						<div className="completion-rate__label">Tasks Completed</div>
					</div>
				</ReportCard>
			</div>

			{/* Task List */}
			<ReportCard title="My Tasks" loading={loading} onRefresh={onRefresh}>
				<ReportTable
					columns={taskColumns}
					data={taskList}
					loading={loading}
					searchable={true}
					exportable={true}
					emptyMessage="No tasks assigned"
				/>
			</ReportCard>
		</div>
	)
}

// Approvals Tab Component
const ApprovalsTab = ({ data, loading }) => {
	const approvals = data?.data || data || {}
	const summary = approvals.summary || {}
	const approvalList = approvals.approvals || approvals.data || []

	const approvalColumns = [
		{ key: 'title', label: 'Task', sortable: true },
		{ key: 'project_name', label: 'Project', sortable: true },
		{ key: 'status', label: 'Status', sortable: true },
		{ key: 'requested_by', label: 'Requested By', sortable: true },
		{ key: 'requested_at', label: 'Requested', sortable: true, format: 'date' },
	]

	return (
		<div className="approvals-tab">
			<div className="metric-cards-grid">
				<MetricCard
					title="Pending Approvals"
					value={summary.pending || 0}
					icon={<Clock size={20} />}
					variant="warning"
					loading={loading}
				/>
				<MetricCard
					title="Approved"
					value={summary.approved || 0}
					icon={<CheckCircle size={20} />}
					variant="success"
					loading={loading}
				/>
				<MetricCard
					title="Rejected"
					value={summary.rejected || 0}
					icon={<AlertTriangle size={20} />}
					variant="danger"
					loading={loading}
				/>
			</div>

			<ReportCard title="Approval History" loading={loading}>
				<ReportTable
					columns={approvalColumns}
					data={approvalList}
					loading={loading}
					searchable={true}
					exportable={true}
					emptyMessage="No approval requests"
				/>
			</ReportCard>
		</div>
	)
}

// Performance Tab Component
const PerformanceTab = ({ data, loading }) => {
	const performance = data?.data || data || {}
	const summary = performance.summary || {}
	const trend = performance.trend || []

	return (
		<div className="performance-tab">
			<div className="metric-cards-grid">
				<MetricCard
					title="Tasks Completed"
					value={summary.tasks_completed || 0}
					subtitle="this period"
					icon={<CheckCircle size={20} />}
					variant="success"
					loading={loading}
				/>
				<MetricCard
					title="Avg Completion Time"
					value={summary.avg_completion_time || '-'}
					icon={<Clock size={20} />}
					variant="info"
					loading={loading}
				/>
				<MetricCard
					title="On-Time Rate"
					value={`${summary.on_time_rate || 0}%`}
					icon={<Calendar size={20} />}
					variant="primary"
					loading={loading}
				/>
				<MetricCard
					title="SLA Compliance"
					value={`${summary.sla_compliance || 0}%`}
					icon={<TrendingUp size={20} />}
					variant={summary.sla_compliance >= 90 ? 'success' : 'warning'}
					loading={loading}
				/>
			</div>

			{trend.length > 0 && (
				<ReportCard title="Completion Trend" loading={loading}>
					<LineChart
						data={trend}
						valueKey="completed"
						showArea={true}
						showDots={true}
						height={300}
						color="var(--color-success)"
					/>
				</ReportCard>
			)}

			<ReportCard title="Performance Summary" loading={loading}>
				<div className="performance-summary">
					<div className="summary-item">
						<User size={20} />
						<div className="summary-item__content">
							<span className="summary-item__label">Role</span>
							<span className="summary-item__value">{summary.role || '-'}</span>
						</div>
					</div>
					<div className="summary-item">
						<FileCheck size={20} />
						<div className="summary-item__content">
							<span className="summary-item__label">Total Tasks</span>
							<span className="summary-item__value">{summary.total_tasks || 0}</span>
						</div>
					</div>
					<div className="summary-item">
						<CheckCircle size={20} />
						<div className="summary-item__content">
							<span className="summary-item__label">Completion Rate</span>
							<span className="summary-item__value">{summary.completion_rate || 0}%</span>
						</div>
					</div>
				</div>
			</ReportCard>
		</div>
	)
}

export default Reports
